import { useCallback, useState } from 'react';
import { useOidcContext } from '../../contexts/OidcContext';
import { api } from '../../api';
import type { GeneratorMessage } from '../../types/apiRequests';

export type GeneratorStep =
  | 'describing'
  | 'analyzing'
  | 'clarifying'
  | 'summary'
  | 'requesting'
  | 'done'
  | 'error';

interface GeneratorState {
  step: GeneratorStep;
  messages: GeneratorMessage[];
  questions: string[];
  summary: string;
  requestId: string;
  errorMessage: string;
}

const INITIAL: GeneratorState = {
  step: 'describing',
  messages: [],
  questions: [],
  summary: '',
  requestId: '',
  errorMessage: '',
};

function toErrorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export const useWorkflowGenerator = () => {
  const { getAccessTokenSilently } = useOidcContext();
  const [state, setState] = useState<GeneratorState>(INITIAL);

  const reset = useCallback(() => setState(INITIAL), []);

  const goToDescribe = useCallback(() => {
    setState((prev) => ({ ...prev, step: 'describing', errorMessage: '' }));
  }, []);

  const submitDescription = useCallback(
    async (text: string) => {
      let snapshot: GeneratorState | null = null;
      setState((prev) => {
        const nextMessages: GeneratorMessage[] = [
          ...prev.messages,
          { role: 'user', content: text },
        ];
        snapshot = { ...prev, messages: nextMessages, step: 'analyzing', errorMessage: '' };
        return snapshot;
      });
      if (!snapshot) return;
      const messages = (snapshot as GeneratorState).messages;
      try {
        const token = await getAccessTokenSilently();
        const result = await api.analyzeWorkflowGeneration(token, { messages });
        if (result.status === 'needs_clarification') {
          const questions = result.questions ?? [];
          setState((prev) => ({ ...prev, questions, step: 'clarifying' }));
        } else {
          setState((prev) => ({ ...prev, summary: result.summary ?? '', step: 'summary' }));
        }
      } catch (err: unknown) {
        setState((prev) => ({ ...prev, step: 'error', errorMessage: toErrorMessage(err) }));
      }
    },
    [getAccessTokenSilently],
  );

  const submitRequest = useCallback(async () => {
    let snapshot: GeneratorState | null = null;
    setState((prev) => {
      snapshot = { ...prev, step: 'requesting', errorMessage: '' };
      return snapshot;
    });
    if (!snapshot) return;
    const { messages, summary } = snapshot as GeneratorState;
    const promptText = messages.map((m) => m.content).join('\n\n');
    try {
      const token = await getAccessTokenSilently();
      const result = await api.createWorkflowGenerationRequest(token, {
        prompt: promptText,
        summary,
      });
      setState((prev) => ({ ...prev, step: 'done', requestId: result.id }));
    } catch (err: unknown) {
      setState((prev) => ({ ...prev, step: 'error', errorMessage: toErrorMessage(err) }));
    }
  }, [getAccessTokenSilently]);

  return { ...state, submitDescription, submitRequest, reset, goToDescribe };
};
